import { IShimmerElement, Shimmer, ShimmerElementType, Spinner, SpinnerSize } from '@fluentui/react';
import { makeStyles, mergeClasses } from '@fluentui/react-components';
import * as React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { CssSelector } from '../../themes/constants/css-selectors';
import { useHorizontalStackStyles, useStackStyles } from '../../themes/styles/flexbox-styles';

const messages = defineMessages({
    loadingAriaLabel: {
        id: 'HomeLoadingState_Loading_AriaLabel',
        defaultMessage: 'Loading your resources',
        description: 'Accessible label for the home page resource loading indicator.',
    },
    loadingMessage: {
        id: 'HomeLoadingState_LoadingMessage_Text',
        defaultMessage: 'Loading your resources...',
        description: 'Message shown while the home page discovers the signed-in user’s resources.',
    },
    loadedMessage: {
        id: 'HomeLoadingState_LoadedMessage_Text',
        defaultMessage: 'Resource loading is complete.',
        description:
            'Accessible announcement that the home page finished its initial resource loading operation. Some resources might not be available.',
    },
    sectionTitle: {
        id: 'HomeLoadingState_SectionTitle_Text',
        defaultMessage: 'Your resources',
        description: 'Heading for placeholder resource cards while the home page loads.',
    },
});

const placeholderCount = 2;

const useContainerStyles = makeStyles({
    root: {
        gap: '16px',
        alignItems: 'start',
    },
});

const useGridStyles = makeStyles({
    root: {
        gap: '12px',
        flexWrap: 'wrap',

        [CssSelector.ScreenSizeXXLarge]: {
            gap: '24px',
        },
    },
});

const useLoadingMessageStyles = makeStyles({
    root: {
        gap: '8px',
        alignItems: 'center',
    },
});

const useScreenReaderOnlyStyles = makeStyles({
    root: {
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: 0,
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: 0,
    },
});

const useCardStyles = makeStyles({
    root: {
        boxSizing: 'border-box',
        height: '406px',
        padding: '16px',
        width: '260px',

        [CssSelector.ScreenSizeSmallAndBelow]: {
            width: '100%',
        },
    },
    preview: {
        marginBottom: '20px',
    },
    line: {
        marginBottom: '14px',
    },
});

const previewShimmer: IShimmerElement[] = [{ type: ShimmerElementType.line, height: 184, width: '100%' }];
const titleShimmer: IShimmerElement[] = [{ type: ShimmerElementType.line, height: 20, width: '70%' }];
const metadataShimmer: IShimmerElement[] = [{ type: ShimmerElementType.line, height: 16, width: '85%' }];

const HomeResourcePlaceholder: React.FC = () => {
    const cardStyles = useCardStyles();

    return (
        <div className={cardStyles.root}>
            <div className={cardStyles.preview}>
                <Shimmer shimmerElements={previewShimmer} />
            </div>
            <div className={cardStyles.line}>
                <Shimmer shimmerElements={titleShimmer} />
            </div>
            <div className={cardStyles.line}>
                <Shimmer shimmerElements={metadataShimmer} />
            </div>
            <div className={cardStyles.line}>
                <Shimmer shimmerElements={metadataShimmer} />
            </div>
            <Shimmer shimmerElements={metadataShimmer} />
        </div>
    );
};

interface HomeLoadingAnnouncementProps {
    isLoading: boolean;
}

export const HomeLoadingAnnouncement: React.FC<HomeLoadingAnnouncementProps> = (
    props: HomeLoadingAnnouncementProps
) => {
    const { isLoading } = props;
    const { formatMessage } = useIntl();
    const screenReaderOnlyStyles = useScreenReaderOnlyStyles();
    const hasAnnouncedLoading = React.useRef(false);
    const [announcement, setAnnouncement] = React.useState('');

    React.useEffect(() => {
        if (isLoading) {
            hasAnnouncedLoading.current = true;
            setAnnouncement(formatMessage(messages.loadingAriaLabel));
        } else if (hasAnnouncedLoading.current) {
            setAnnouncement(formatMessage(messages.loadedMessage));
        }
    }, [formatMessage, isLoading]);

    return (
        <span aria-live="polite" className={screenReaderOnlyStyles.root}>
            {announcement}
        </span>
    );
};

interface HomeResourcePlaceholdersProps {
    count?: number;
}

export const HomeResourcePlaceholders: React.FC<HomeResourcePlaceholdersProps> = (
    props: HomeResourcePlaceholdersProps
) => {
    const { count = placeholderCount } = props;
    const gridStyles = useGridStyles();
    const horizontalStackStyles = useHorizontalStackStyles();

    return (
        <div aria-hidden={true} className={mergeClasses(horizontalStackStyles.root, gridStyles.root)}>
            {Array.from({ length: count }, (_, index) => (
                <HomeResourcePlaceholder key={index} />
            ))}
        </div>
    );
};

export const HomeLoadingState: React.FC = () => {
    const containerStyles = useContainerStyles();
    const loadingMessageStyles = useLoadingMessageStyles();
    const horizontalStackStyles = useHorizontalStackStyles();
    const stackStyles = useStackStyles();

    return (
        <section className={mergeClasses(stackStyles.root, containerStyles.root)}>
            <h2 className={stackStyles.item}>
                <FormattedMessage {...messages.sectionTitle} />
            </h2>
            <div className={stackStyles.item}>
                <div className={mergeClasses(horizontalStackStyles.root, loadingMessageStyles.root)}>
                    <Spinner size={SpinnerSize.small} />
                    <FormattedMessage {...messages.loadingMessage} />
                </div>
            </div>
            <div className={stackStyles.item}>
                <HomeResourcePlaceholders />
            </div>
        </section>
    );
};

export default HomeLoadingState;
